import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

interface SecurityEventDetails {
  [key: string]: any;
}

type SecurityEventType = 
  | 'login_success' 
  | 'login_failed' 
  | 'logout' 
  | 'password_reset_requested'
  | 'password_changed'
  | 'profile_updated'
  | 'sensitive_data_accessed'
  | 'admin_action_performed'
  | 'organization_access_attempted'
  | 'application_error'
  | 'api_error'
  | 'unhandled_error'
  | 'system_settings_changed'
  | 'public_settings_changed'
  | 'smtp_configuration_changed'
  | 'feature_toggle_changed'
  | 'rate_limit_exceeded';

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export const useSecurityMonitoring = (user?: User | null) => {

  const logSecurityEvent = useCallback(async (
    eventType: SecurityEventType,
    details?: SecurityEventDetails,
    riskLevel: RiskLevel = 'low',
    resourceType?: string,
    resourceId?: string,
    organizationId?: string
  ) => {
    try {
      const enhancedDetails = {
        ...details,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
      };

      const { error } = await supabase.functions.invoke('log-security-event', {
        body: {
          p_event_type: eventType,
          p_resource_type: resourceType || null,
          p_resource_id: resourceId || null,
          p_organization_id: organizationId || null,
          p_details: enhancedDetails,
          p_risk_level: riskLevel
        }
      });

      if (error) {
        console.error('[Security] Failed to log security event:', error);
      } else {
        console.log(`[Security] Event logged: ${eventType} (${riskLevel})`);
      }
    } catch (error) {
      console.error('[Security] Error logging security event:', error);
    }
  }, []);

  const logLoginAttempt = useCallback(async (success: boolean, email?: string) => {
    await logSecurityEvent(
      success ? 'login_success' : 'login_failed',
      { email, timestamp: new Date().toISOString() },
      success ? 'low' : 'medium'
    );
  }, [logSecurityEvent]);

  const logSensitiveDataAccess = useCallback(async (
    dataType: string,
    resourceId?: string,
    organizationId?: string
  ) => {
    await logSecurityEvent(
      'sensitive_data_accessed',
      { 
        data_type: dataType,
        access_timestamp: new Date().toISOString()
      },
      'medium',
      dataType,
      resourceId,
      organizationId
    );
  }, [logSecurityEvent]);

  const logAdminAction = useCallback(async (
    action: string,
    targetResourceType?: string,
    targetResourceId?: string,
    details?: SecurityEventDetails
  ) => {
    await logSecurityEvent(
      'admin_action_performed',
      {
        action,
        target_resource_type: targetResourceType,
        target_resource_id: targetResourceId,
        timestamp: new Date().toISOString(),
        ...details
      },
      'low',
      targetResourceType,
      targetResourceId
    );
  }, [logSecurityEvent]);

  const logError = useCallback(async (
    error: Error,
    errorType: 'application_error' | 'api_error' | 'unhandled_error' = 'application_error',
    context?: {
      componentName?: string;
      componentStack?: string;
      userAction?: string;
      route?: string;
      additionalInfo?: any;
    }
  ) => {
    // Filter out development/localhost errors in production
    const isDev = import.meta.env.DEV;
    if (isDev) {
      console.log('[Dev] Error not logged to security system in development:', error);
      return;
    }

    // Determine risk level based on error type
    let riskLevel: RiskLevel = 'medium';
    if (errorType === 'unhandled_error') {
      riskLevel = 'critical';
    } else if (error.message?.toLowerCase().includes('security') || 
               error.message?.toLowerCase().includes('unauthorized')) {
      riskLevel = 'high';
    }

    // Parse stack trace for better readability
    const stackLines = error.stack?.split('\n').slice(0, 10) || [];
    const formattedStack = stackLines.join('\n');

    // Extract file and line info from stack
    const stackMatch = error.stack?.match(/at .+ \((.+):(\d+):(\d+)\)/);
    const filePath = stackMatch?.[1] || 'unknown';
    const lineNumber = stackMatch?.[2] ? parseInt(stackMatch[2]) : undefined;
    const columnNumber = stackMatch?.[3] ? parseInt(stackMatch[3]) : undefined;

    const errorDetails = {
      error_type: errorType,
      error_message: error.message,
      error_name: error.name,
      stack_trace: formattedStack,
      component_name: context?.componentName,
      component_stack: context?.componentStack,
      user_action: context?.userAction,
      route: context?.route || window.location.pathname,
      file_path: filePath,
      line_number: lineNumber,
      column_number: columnNumber,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      is_dev: isDev,
      ...context?.additionalInfo
    };

    await logSecurityEvent(
      errorType,
      errorDetails,
      riskLevel,
      'error',
      undefined,
      undefined
    );
  }, [logSecurityEvent]);

  return {
    logSecurityEvent,
    logLoginAttempt,
    logSensitiveDataAccess,
    logAdminAction,
    logError
  };
};